import { BaseLanguage } from "./baseLang.service";
import { SemanticErrorCause, SyntaxErrorCause } from "app/xlogo-parser";

export class ItalianService extends BaseLanguage {
  //#################################################################################
  //
  // This service holds hashmap containing all german strings used in our platform
  // When translating, please keep keys (first argument) same, while
  // adapting the values (seconds argument).
  //
  //#################################################################################

  constructor() {
    super();

    this.populateStaticStrings();
    this.defineLoginStrings();
    this.defineExamStrings();
    this.defineOnlineStorageStrings();
    this.defineQickStartStrings();
    this.defineRuntimeErrorStrings();
    this.defineSemanticErrorStrings();
    this.defineSyntaxErrorStrings();
    this.defineDebugWatchStrings();
    this.defineDebugControls();
    this.defineTooltips();
    this.defineProgressStrings();
    this.defineRobotStrings();
    this.defineUIstrings();
    this.defineMenuStrings();
  }

  // strings used in menu
  private defineMenuStrings() {
    this.define("menu", "Menu");
    this.define("saveFile", "Salva programma");
    this.define("loadFile", "Carica programma");
    this.define("saveCanvas", "Salve immagine");
    this.define("fullscreen", "Schermo intero");
    this.define("newfeatures", "Cosa c'è di nuovo?");
    this.define("Cheatsheet", "Appunti");
    this.define("background", "Salvaschermi");
    this.define("save-as-png", "Salva come png");
    this.define("save-as-svg", "Salva come svg");
    this.define("Robot", "Robot");
  }

  // user interface strings: history, header, input and general UI
  private defineUIstrings() {
    this.define("enterCommand", "Inserisci i comandi qui");
    this.define("cancel", "interrompi");
  }

  // Robots interaction strings for BlueBot and Root
  private defineRobotStrings() {
    this.define("WhichRobotUsed", "Quale robot usi?");
    this.define("disconnectBlueBot", "BlueBot è già collegato. Vuoi scollegarlo?");
    this.define("RobotConnected", "Root è già collegato. Vuoi scollegarlo?");
    this.define("ConnectionNotPossible", "Il collegamento non è possibile");
    this.define("ConnectionError", "Il computer e/o il browser non supporta i dispositivi Bluetooth Low Energy. Aggiornare i driver Bluetooth e testare di nuovo con un altro dispositivo o browser.");
    this.define("RobotDisconnect", "Si, scollegare");
  }

  //Progress strings shown in header while execution
  private defineProgressStrings() {
    this.define("executingVisitor", "Esecuzione del comando in corso...");
    this.define("init", "Inizializzazione");
  }

  // Strings related to tooltips in header
  private defineTooltips() {
    this.define("tooltipButtonStop", "Interrompi il programma in corso.");
    this.define("tooltipButtonDebug", "Esegui il tuo programma passo per passo.");
    this.define("tooltipButtonForward", "Vai un passo indietro.");
    this.define("tooltipButtonBackward", "Vai un passo in avanti.");
    this.define("tooltipButtonSpeed", "Adatta la velocità d'esecuzione");
    this.define("tooltipButtonLanguage", "Cambia le impostazioni della lingua");
    this.define("tooltipButtonTurtle", "Cambia l'icona della tartaruga");
  }

  // Strings related to debug controls in header
  private defineDebugControls() {
    this.define("buttonDebugToggle", "Passo per passo");
    this.define("buttonForward", "Avanti");
    this.define("buttonBackward", "Indietro");
  }

  // Strings related to ebug watch
  private defineDebugWatchStrings() {
    this.define("watchPenDown", "Penna giù:");
    this.define("watchErasing", "Cancellare:");
    this.define("watchVisible", "Visibile:");
    this.define("watchColor", "Colore della penna:");
    this.define("watchWidth", "Spessore della penna:");
    this.define("watchDirection", "Direzione:");
  }

  // Strings related to syntactic errors
  private defineSyntaxErrorStrings() {
    this.define(SyntaxErrorCause[SyntaxErrorCause.EXTRANEOUS_INPUT], "Carattere superfluo {extraneousToken}.");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISMATCHED_INPUT], "Carattere non valido {mismatchedToken}.");
    this.define(SyntaxErrorCause[SyntaxErrorCause.MISSING_INPUT], "Carattere mancante {missingToken} prima di {missingPosition}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.NO_VIABLE_ALT], "Codice del programma errato alla posizione {no_viable_alt_position}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.UNKNOWN], "Si è verificato un errore imprecisato: {msg}");
    this.define(SyntaxErrorCause[SyntaxErrorCause.FAILED_PREDICATE], "{msg}"); //this one should not be used. It indicates an unexpected error on our side
  }

  // Strings related to semantic errors
  private defineSemanticErrorStrings() {
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_NAME], "Il nome dato a questo programma è già stato utilizzato");
    this.define(SemanticErrorCause[SemanticErrorCause.DOUBLE_DECLARATION_PROGRAM_PARAMETER], "Il nome dato a questo parametro è già stato utilizzato: {parameter}");
    this.define(SemanticErrorCause[SemanticErrorCause.INVALID_PROGRAM_NAME], "Il nome dato a questo programma non è consentito");
    this.define(SemanticErrorCause[SemanticErrorCause.NOT_ENOUGH_ARGUMENTS], "Mancano parametri in '{program}'. E' necessario/Sono necessari {number_of_arguments} parametro/i.");
    this.define(SemanticErrorCause[SemanticErrorCause.TOO_MANY_ARGUMENTS], "Vengono specificati troppi parametri in '{program}'. È necessario/Sono necessari {number_of_arguments} parametro/i."); 
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_PROGRAM], "Il programma '{program}' non esiste (o esiste, ma contiene degli errori).");
    this.define(SemanticErrorCause[SemanticErrorCause.NO_SUCH_VARIABLE], "La variabile o il parametro '{variable}' non è ancora stata/o definita/o."); 
  }

  // Strings related to runtime errors
  private defineRuntimeErrorStrings() {
    this.define("runtime_error_unknown_program", "Il programma '{program}' non esiste (o esiste, ma contiene degli errori).");
    this.define("runtime_error_unknown_function", "La funzione {function_name} non esiste. Si tratta di un bug. Ti preghiamo di informarci."); 
    this.define("runtime_error_unknown_builtin", "Il comando '{command}' non esiste. Si tratta di un bug. Ti preghiamo di informarci."); 
    this.define("runtime_error_unknown_operator", "L'operatore '{operator}' non esiste. Si tratta di un bug. Ti preghiamo di informarci.");
    this.define("runtime_error_generic_invalid_argument", "Parametro non valido per '{program}': {argument}");

    this.define("runtime_error_division_by_zero", "Non è possibile dividere per 0");
    this.define("runtime_error_generic_not_in_range", "Il valore per {program} deve essere compreso tra {lower_bound} e {upper_bound}");
    this.define("runtime_error_invalid_argument_tan", "Il valore non può essere della forma (0.5 + k) * PI");
    this.define("runtime_error_mod_by_zero", "Calcolare 'n mod 0' non è possibile");
    this.define("runtime_error_invalid_argument_colornumber", "Il colore specificato dal numero non esiste: {color_number}");
    this.define("runtime_error_invalid_argument_colorname", "Il colore con il nome specificato non esiste:{color_name}");
    this.define("runtime_error_invalid_argument_rgb", "I colori vengono specificati con dei codici numerici: {color}");
    this.define("runtime_error_rgb_out_of_range", "Il colore specificato non è valido: {color}");
    this.define("runtime_error_sqrt_negative_number", "Non è possibile calcolare la radice di numeri negativi");
    this.define("runtime_error_log10_negative_number", "Non è possibile calcolare il logaritmo di numeri negativi");
    this.define("runtime_error_setpw_negative_number", "Lo spessore della penna non può essere negativo");
    this.define("runtime_error_wait_negative_number", "Non è possibile aspettare una quantità di tempo negativa");
    this.define("runtime_error_circle_negative_number", "Non è possibile disegnare un cerchio con raggio negativo");
  }

  // Strings related to quickstarter
  private defineQickStartStrings() {
    this.define("tutorialtype", "Scegli un tutorial.");
    this.define("doyouneedhelp", "Hai bisogno di aiuto?");
    this.define("firststeps", "Primi passi");
    this.define("definenewcommands", "Definire nuovi comandi");
    this.define("debugger", "Esecuzione passo per passo");
    this.define("intro1", "Willkommen in XLogoOnline. In diesem Tutorial lernst du, farbige Muster zu programmieren.");
    this.define("intro2", "Schau, die Schildkröte zeichnet ein Mandala! Was wir sehen, ist das Resultat eines Programms.");
    this.define("intro3", "Programme bestehen aus Befehlen. Zwei solche Befehle sind FD und BK (die wir verwenden, um Linien zeichnen). RT und LT drehen die Schildkröte nach rechts oder links.");
    this.define("intro4", "Probier es aus! Klicke ins Feld und bestätige den Befehl mit ENTER");
    this.define("intro5", "Die Schildkröte führt jeden deiner Befehle aus. Das Resultat siehst du hier auf der Zeichenfläche");
    this.define("intro6", "Lösche die Zeichnung mit dem Befehl CS.");
    this.define("intro7", "CS löscht alle Linien und setzt die Schildkröte zurück in die Mitte");
    this.define("intro8", "Progamme können auch aus mehreren Befehlen bestehen. Finde heraus, was hier gezeichnet wird!");
    this.define("intro9", "Alle bereits ausgeführten Programme werden hier der Reihe nach aufgelistet.");
    this.define("intro10", "Mit den PFEIL-TASTEN " + String.fromCharCode(8679) + " und " + String.fromCharCode(8681) + " können frühere Programme (ohne sie nochmals zu schreiben) wieder in die Eingabezeile geladen werden.");
    this.define("intro11", "Was geschieht eigentlich, wenn wir einen Fehler machen?");
    this.define("intro12", "Dieses Programm ist fehlerhaft. Siehst du den Fehler? Teste, was die Schildkröte mit falschen Programmen macht.");
    this.define("intro13", "Die Schildkröte hat die Anweisung nicht verstanden. Anstatt einer Zeichnung erhalten wir einen Hinweis auf den Fehler und dessen Ursache (hier in orange).");
    this.define("intro14", "Los geht's. Viel Spass beim Programmieren!");
    this.define("editor1", "In diesem Tutorial zeigen wir dir, wie du neue Befehle definierst und verwendest.");
    this.define("editor2", "Schau dir dieses komplexe Programm an! Wer erratet, was das Programm tut?");
    this.define("editor3", "Was? So ein kompliziertes Programm, bloss um vier Quadrate zu zeichnen?!");
    this.define("editor4", "Kann man das nicht viel einfacher machen, zum Beispiel so?");
    this.define("editor5", "Leider nicht; den Befehl QUADRAT kennt die Schildkröte nämlich nicht... Lasst uns also einen neuen Befehl definieren!");
    this.define("editor6", "Neue Befehle definieren wir hier. Dieses Feld nennen wir Editor.");
    this.define("editor7", "'TO' QUADRAT 'END' definiert einen neuen Befehl mit dem Namen QUADRAT.");
    this.define("editor8", "In demselben Fenster können auch mehrere Befehle definiert werden.");
    this.define("editor9", "Hoppla! Siehst du das rote Kreuz in Zeile 5? Es zeigt an, dass sich auf dieser Zeile ein Fehler versteckt. Was könnte es sein? Derselbe Befehl QUADRAT wurde mehrfach definiert. (Tipp: Nach diesem Tutorial kannst du mit der Maus übers Kreuz fahren und erfährst weitere Details zum Fehler)");
    this.define("editor10", "Was genau soll eigentlich geschehen, wenn der Befehl QUADRAT aufgerufen wird? Wir notieren die entsprechenden Befehle zwischen TO und END");
    this.define("editor11", "Lasst uns testen, was der neue Befehl bewirkt. Was denkst du? Probier es aus.");
    this.define("editor12", "Ein Quadrat, und das mit nur einem einzigen Befehl! Praktisch! Damit lassen sich nun auch kompliziertere Bilder ganz einfach zeichnen...");
    this.define("editor13", "Gratulation! Du hast deinen ersten eigenen Befehl definiert (im Editor) und aufgerufen. Viel Spass beim programmieren!");
  }

  // Strings related to storage functionality
  private defineOnlineStorageStrings() {
    this.define("loginButton", "Login");
    this.define("logoutButton", "Logout");
    this.define("registerButton", "Registrare");
    this.define("loginTitle", "Login");
    this.define("username", "Nome utente");
    this.define("password", "password (opzionale)");
    this.define("successfulLoginTitle", "Login effettuato");
    this.define("successfulLoginText", "Sei connesso come l'utente ");
    this.define("failLoginTitle", "Login errato");
    this.define("tryAgain", "Riprova!");
    this.define("registerTitle", "Registra come nuovo utente");
    this.define("repeatPassword", "ripeti la password");
    this.define("passwordMisatchTitle", "Le password non corrispondono");
    this.define("passwordMisatchText", "Le password non corrispondono. Riprova ancora!");
    this.define("successfulRegistrationTitle", "Registrazione riuscita");
    this.define("successfulRegistrationText", "La registrazione è riuscita, sei connesso come ");
    this.define("serversuccess", "Successo");
    this.define("unknownuser", "Il nome utente non è esistente");
    this.define("wrongpassword", "password errata per questo nome utente");
    this.define("usernamealreadytaken", "Nome utente è già esistente. Per favore, scegliere un'altro nome utente.");
    this.define("Ihaveread", "Ho letto e accetto ");
    this.define("datenschutz", "le condizioni sulla privacy");
    this.define("aswellas", "e");
    this.define("terms", "i termini e le condizioni");
    this.define("readandaccepted", "");
    this.define("toomuchtext", "Questo documento è troppo grande");
    this.define("toomuchtexthint", "Dividi la tua soluzione in parti piu` corte");
    this.define("documentexists", "Il documento è già esistente");
    this.define("overwrite", "Desideri sovrascrivere il documento?");
    this.define("writtensuccessfully", "Il documento è stato salvato");
    this.define("doYouWantToLogOut", "Vuoi disconnetterti?");
    this.define("didYouSaveYourPrograms", "Hai salvato i programmi?");
    this.define("announceTitle", "Ricerca esame");
  }

  // Strings related to Exam mode
  private defineExamStrings() {
    this.define("handInTask", "Invia compito");
    this.define("choseAnOption", "Scegliere una delle seguenti opzioni");
    this.define("startExamMode", "Enigmi");
    this.define("waitingForExamToStart", "Pronti. In attesa del lancio.");
    this.define("examFinished", "Il concorso e' finito.");
    this.define("checkingForExam", "Concorso di carico.....");
    this.define("cancelExamModeButton", "abortire");
    this.define("enterExamModeButton", "Concorso di carico.....");
    this.define("infoRecognition", "Parola di identificazione del concorso.");
    this.define("notauthorizedTitle", "Login per favore");
    this.define("notauthorizedText", "Per sostenere, è necessario effettuare il login.");
  }

  // Strings related to login
  private defineLoginStrings() {
    this.define("notingtosave", "Non c'e' niente da salvare.");
    this.define("howtosave", "Come  preferisci salvare?");
    this.define("save", "Salvare");
    this.define("locally", "Sul mio computer");
    this.define("online", "Online");
    this.define("load", "Caricare programmi");
    this.define("noconnection", "Connessione fallita");
    this.define("filename", "Nome del mio file");
    this.define("login", "Login");
    this.define("chapterx", "Capitolo X Esercizio Y");
    this.define("loadprogram", "Carica programma:");
    this.define("chooseprogram", "Scegli un programma");
    this.define("youcanloadandstore", "Ora sei in grado di salvare e ricaricare i tuoi programmi online");
    this.define("logoutsuccess", "Ti sei disconnesso");
  }

  // These strings are static and do not need to be translated ever!
  populateStaticStrings() {
    this.define("XLogoOnline", "XLogoOnline");
  }
}



// WEBPACK FOOTER //
// ./src/app/languages/italian.service.ts